import { createSelector, createSlice } from "@reduxjs/toolkit";
import { ensureInteger } from "../common/utils/generalUtils";
import { ensureArray } from "../common/utils/arrayUtilities";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, DefaultFailureAction, DefaultSuccessAction } from "../common/reducers/utilities";
import { complexTableQuery } from "../common/utils/tableUtilities";
import { markFetched } from "../common/hooks/initHook";

const key = supplier => ensureInteger(supplier.id);

export const supplierSlice = createSlice({
    name: 'suppliers',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            state.data = ensureArray(action.payload).reduce((obj, supplier) => { obj[key(supplier)] = supplier; return obj; }, {});
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(supplier => state.data[key(supplier)] = supplier);
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(supplier => delete state.data[key(supplier)]);
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => supplierSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'supplier_one', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'supplier_one');

const fetchAllSuppliers = network => network.get('/api/v1/suppliers', supplierSlice.actions.set, visualizeFailure('get'));

export const createSupplier = (network, supplier) => network.post('/api/v1/supplier', supplier, visualizeSuccess('create', supplierSlice.actions.addOrUpdate), visualizeFailure('create'));
export const updateSupplier = (network, delta) => network.put('/api/v1/supplier', delta, supplierSlice.actions.addOrUpdate, visualizeFailure('update'));
export const deleteSupplier = (network, id) => network.delete('/api/v1/supplier/' + window.encodeURIComponent(id), visualizeSuccess('remove', supplierSlice.actions.remove), visualizeFailure('remove'));

// #endregion

// #region selectors
const selectSupplierData = state => state.suppliers.data;
const selectAllSuppliers = createSelector([selectSupplierData], suppliers => Object.values(suppliers))
const selectSupplier = id => createSelector([selectSupplierData], suppliers => suppliers[id]);
const selectSuppliers = ids => createSelector([selectSupplierData], suppliers => ids.reduce((res, id) => {
    if (suppliers[id]) {
        res.push(suppliers[id]);
    }
    return res;
}, []));
const selectSearchSupplier = search => createSelector([selectSupplierData], suppliers => {
    let ignoreFields = Object.keys(search).filter(f => search[f] === undefined || search[f] === null || (Array.isArray(search[f]) && search[f].length === 0));
        let results = Object.values(suppliers);
        return complexTableQuery(results, search, { ignoreFields });
}
)

// #endregion

const listen = {
    supplierCreated: (supplier, { dispatch, websocket }) => {
        markFetched(websocket, 'supplier', key(supplier))
        dispatch(supplierSlice.actions.addOrUpdate(supplier))
    },
    supplierUpdated: (supplier, { dispatch }) => dispatch(supplierSlice.actions.addOrUpdate(supplier)),
    supplierRemoved: (supplier, { dispatch }) => dispatch(supplierSlice.actions.remove(supplier))
}
// #region listeners

const initSuppliers = { run: fetchAllSuppliers, listen, immortal: true, name: 'supplier' };
export const [useAllSuppliers] = dataHookFactory(selectAllSuppliers, initSuppliers);
export const [useSuppliers] = dataHookFactory(selectSuppliers, ids => initSuppliers);
export const [useSupplier, realizeSupplier] = dataHookFactory(selectSupplier, id => initSuppliers);
export const [useSupplierSearch] = dataHookFactory(selectSearchSupplier, search => initSuppliers);

const fullRoles = ['administrator', 'equipment manager'];
const readRoles = ['auditor', 'equipment auditor'];
export const canUpdateSupplier = (credentials) => checkPermissions(credentials, fullRoles);
export const canCreateSupplier = (credentials) => checkPermissions(credentials, fullRoles);
export const canRemoveSupplier = canUpdateSupplier;
export const canViewSupplier = (credentials) => checkPermissions(credentials, fullRoles.concat(readRoles));

const checkPermissions = (credentials, requiredRoles) => {
    return credentials.hasRole(requiredRoles);
}

const reducer = { name: supplierSlice.name, reducer: supplierSlice.reducer }
export default reducer;